import styled, { css } from 'styled-components';

import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import {
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types/Theme';

import {
  getCustomComponentProperty,
  styledGetCustomComponentProperty,
  SUPPORTED_ICON_POSITIONS,
} from '../../helpers/theme';
import { fontStyle } from '../../theme/helpers/fontStyle';
import { Tooltip } from '../Tooltip';

import {
  getBackgroundColor,
  getBorder,
  getBorderRadius,
  getButtonPadding,
  getButtonsTextTransform,
  getColor,
  getFontSize,
  getFontWeight,
  getHeight,
  getIconColor,
  getMinHeight,
  getOnHoverBackgroundColor,
  getOnHoverBorder,
  getOnHoverColor,
  getWhiteSpace,
  getWidth,
  handleUnderlineProps,
} from './Button.helpers';
import { CountStyledProps, InnerButtonStyledProps } from './Button.types';
import {
  handleHollowProps,
  handleHoverAsHollow,
} from './helpers/getHollowButtonStylesFromProps';
import getNakedButtonStylesFromProps from './helpers/getNakedButtonStylesFromProps';

const handleSquareIconButton = (props: InnerButtonStyledProps) => {
  const { $isSquareIconButton, theme, small, tertiary } = props;

  if (!$isSquareIconButton) {
    return null;
  }

  const height = small
    ? getCustomComponentProperty({
        theme,
        componentName: SUPPORTED_COMPONENTS.SMALL_DEFAULT_BUTTONS,
        property: SUPPORTED_PROPERTIES.HEIGHT,
      })
    : getCustomComponentProperty({
        theme,
        componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
        property: SUPPORTED_PROPERTIES.HEIGHT,
      });

  const color = getIconColor(props);
  const onHoverColor = getOnHoverColor(props);

  return css<InnerButtonStyledProps>`
    & svg {
      color: ${color};
      width: ${spacing(5)};
      height: ${spacing(5)};
    }

    &:hover svg {
      color: ${() => {
        if (tertiary) {
          return null;
        }

        return onHoverColor;
      }};
    }

    width: ${height};
    padding: 0;
    height: ${height};
    max-height: ${height};
    flex-direction: column;
    justify-content: center;
    & > span {
      width: 100%;
      height: 100%;
    }
  `;
};

const handleMobileView = () => {
  return css<InnerButtonStyledProps>`
    @media screen and (max-width: 576px) {
      width: ${({ fluid }) => (fluid ? '100%' : 'auto')};
      & > button {
        width: 100%;
      }
    }
  `;
};

const handleIconButton = ({
  $isSquareIconButton,
  icon,
  position,
}: InnerButtonStyledProps) => {
  if ($isSquareIconButton || icon) {
    return null;
  }

  const defaultStyle = css`
    & svg {
      color: ${getColor};
      flex-shrink: 0;
      max-height: ${spacing(5)};
      max-width: ${spacing(5)};
      transition-property: fill;
      transition-duration: 0.3s;
    }

    &:focus,
    &:active,
    &:hover {
      & svg {
        color: ${getColor};
      }
    }
  `;

  if (!position) {
    return css`
      ${defaultStyle};
    `;
  }

  if (position === SUPPORTED_ICON_POSITIONS.RIGHT) {
    return css`
      & > span > svg,
      & > svg {
        margin-left: ${spacing('sm')};
      }

      ${defaultStyle};
    `;
  }

  return css`
    & > span > svg,
    & > svg {
      margin-right: ${spacing('sm')};
    }

    ${defaultStyle};
  `;
};

export const buttonStyleMixin = css<InnerButtonStyledProps>`
  display: flex;
  justify-content: center;
  flex-direction: column;

  box-sizing: border-box;
  font-size: ${getFontSize};
  text-align: center;
  letter-spacing: 0;
  outline: 0;

  padding: ${getButtonPadding};
  font-weight: ${getFontWeight};
  text-transform: ${getButtonsTextTransform};

  height: ${getHeight};
  width: ${getWidth};

  min-width: ${({ $isIcon, $isOnlyIcon, $isMinimal, small }) => {
    if ($isMinimal || small) {
      return '0';
    }

    if (!$isIcon) {
      return '85px';
    }

    if ($isOnlyIcon) {
      return undefined;
    }

    return styledGetCustomComponentProperty({
      componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
      property: SUPPORTED_PROPERTIES.HEIGHT,
    });
  }};
  min-height: ${getMinHeight};

  position: relative;
  overflow: hidden;

  cursor: ${(props: InnerButtonStyledProps) => {
    if (props.isLoading) {
      return 'progress';
    }
    if (props.disabled) {
      return 'not-allowed';
    }

    return 'pointer';
  }};

  border-radius: ${(props: InnerButtonStyledProps) =>
    props.rounded ? '100px' : null};
  border-radius: ${getBorderRadius};

  border-top-left-radius: ${({ $isSuffix }) => ($isSuffix ? 0 : null)};
  border-bottom-left-radius: ${({ $isSuffix }) => ($isSuffix ? 0 : null)};

  color: ${getColor};

  & > span {
    display: flex;
    flex-direction: row;
  }

  background-color: ${getBackgroundColor};
  border: ${getBorder};

  white-space: ${getWhiteSpace};

  &:active {
    transform: ${(props: InnerButtonStyledProps) =>
      props.disabled ? null : `scale(${props.theme.interactions.activeScale})`};
    opacity: ${({ disabled }: InnerButtonStyledProps) =>
      disabled ? null : 0.8};
  }
  &:hover {
    color: ${getOnHoverColor};
    border: ${getOnHoverBorder};

    background-color: ${({ naked }) =>
      naked ? 'transparent' : getOnHoverBackgroundColor};
  }
  &:focus {
    color: ${getOnHoverColor};
    border: ${getOnHoverBorder};
    background-color: ${({ naked }) =>
      naked ? 'transparent' : getOnHoverBackgroundColor};
  }

  ${handleMobileView};

  ${handleSquareIconButton};
  ${handleHoverAsHollow};
  ${handleHollowProps};
  ${handleUnderlineProps};
  ${getNakedButtonStylesFromProps};
  ${handleIconButton};

  transition: border 500ms;
  transition-property:
    background-color, transform, opacity, color, padding-left, border-color;
  transition-duration: 0.5s;

  & span {
    width: ${({ $isIcon }) => ($isIcon ? 'auto' : '100%')};
    margin: ${({ $isIcon }) => ($isIcon ? 'auto' : null)};
  }

  scale: ${({ $isPressed }) => ($isPressed ? 0.95 : 1)};
`;

export const $InnerButtonStyled = styled.button<InnerButtonStyledProps>`
  ${buttonStyleMixin};
`;

export const $Count = styled.div<CountStyledProps>`
  position: absolute;
  right: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${spacing(3)};
  height: ${spacing(3)};
  padding-inline: ${spacing(0.5)};
  border-radius: ${({ theme }) => theme.tw.radius.sm};
  background-color: ${({
    theme,
    primary,
    secondary,
    tertiary,
    success,
    danger,
    alert,
  }) => {
    return getColor({
      theme,
      primary,
      secondary,
      tertiary,
      success,
      danger,
      alert,
    });
  }};
  color: ${({ theme }) => theme.colors.white};
  ${fontStyle('xxs')};
`;

export const $LockedTooltip = styled(Tooltip)`
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing('sm')};
`;

export const $LockedContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing('sm')};
`;
